import {
  AuditTrailsRowsData,
  BatchAndExpiryManagementRowsData,
  BatchWiseStockApiResponse,
  ManualStockAdjustmentRowsData,
  TransformedBatch,
} from "../../../types/types";
import { ChannelkartNetworkGet } from "../../../services/networkService";
import { clearAllDataFromLocalDb, insertDataIntoLocalDB } from "../../../services/dbService";
import { Product } from "../../createSecondaryOrder/models";
import { EnvironmentRegistry } from "../../../services/registry/environmentRegistry";

const transformBatchData = (apiData: BatchWiseStockApiResponse[]): TransformedBatch[] => {
  const batchMap: { [key: string]: TransformedBatch } = {};
  apiData.forEach((item) => {
    const { batchId, skuCode, quantityType, ...rest } = item;
    const skuBatchKey = `${skuCode}-${batchId}`;
    if (!batchMap[skuBatchKey]) {
      batchMap[skuBatchKey] = {
        id: skuBatchKey,
        batchId: batchId,
        skuCode,
      };
    }
    batchMap[skuBatchKey][quantityType] = { ...rest };
  });
  return Object.values(batchMap);
};

export const setBatchWiseStockDataInDb = async (selectedDistributor?: string, type: string = "normal") => {
  try {
    // Get user context
    const authContext = JSON.parse(localStorage.authContext);
    const currentUser = authContext.user;
    const currentDesignation = currentUser.designation || [];
    
    // Determine supplier - use selected distributor for company or admin users if available
    const isCompany = Array.isArray(currentDesignation)
      ? currentDesignation.includes("company")
      : currentDesignation === "company";

    const isAdmin = Array.isArray(currentDesignation)
      ? currentDesignation.includes("admin")
      : currentDesignation === "admin";

    const isProjectadmin = Array.isArray(currentDesignation)
      ? currentDesignation.includes("projectadmin")
      : currentDesignation === "projectadmin";

    const reportadmin = Array.isArray(currentDesignation)
      ? currentDesignation.includes("reportadmin")
      : currentDesignation === "reportadmin";

    const isCompanyOrAdmin = isCompany || isAdmin || isProjectadmin || reportadmin;

    const useSelectedDistributor = isCompanyOrAdmin && selectedDistributor;
    
    const supplier = useSelectedDistributor 
      ? selectedDistributor 
      : currentUser.loginId;
      
    let page = 0;
    let allTransformedData = [];
    let hasMoreData = true;
    
    while (hasMoreData) {
      const response = await ChannelkartNetworkGet(
        `${EnvironmentRegistry.getStockBaseUrl()}/consolidatedStock/getGroupedBatchWiseStockInfo?supplier=${supplier}&minimalResponse=true&index=${page}${type ? "&type=" + type : ""}`
      );
      
      const content = response.data.content ?? [];
      
      if (content.length === 0) {
        hasMoreData = false;
        break;
      }
      
      const transformedData = transformBatchData(content);
      allTransformedData.push(...transformedData);
      
      page++;
    }
    
    await clearAllDataFromLocalDb("BatchWiseStockData");
    await insertDataIntoLocalDB("BatchWiseStockData", allTransformedData);
    
    return allTransformedData;

  } catch (error) {
    console.log("Error calling Batch wise stock api", error);
    throw new Error();
  }
};


type stockDataType =
  | BatchAndExpiryManagementRowsData
  | ManualStockAdjustmentRowsData
  | AuditTrailsRowsData;

export const getMergedCatalogueAndBatchIdData = <T extends stockDataType>(
  stockData: T[],
  productsMap: Record<string, Product>
) => {
  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
  return stockData.map((item: T) => {
    const product = productsMap[item.skuCode];
    return {
      ...item,
      skuDescription: product?.skuDescription ?? "",
      brand: product?.brand ?? "",
      category: product?.category ?? "",
      case2Piece: product?.case2Piece ?? 1,
      shelfLife: product?.shelfLifeDays ?? 0,
      uom:ConfigDictionary.orderUomCheck  === "true" ? (product?.uom ?? "") : "all",
      otherUnitName: product.otherUnitName
    };
  });
};

export function transformToBatchwiseRowsData(
  batchWiseStockData: TransformedBatch[]
): BatchAndExpiryManagementRowsData[] {
  let batchIdWiseStockData: BatchAndExpiryManagementRowsData[] = [];
  batchWiseStockData.forEach((item: TransformedBatch) => {
    batchIdWiseStockData.push({
      id: item.batchId + item.skuCode,
      skuCode: item.skuCode,
      batchId: item.batchId,
      adjustmentDate:
        item.caseQty?.lastModifiedTime ??
        item.pieceQty?.lastModifiedTime ??
        item.otherQty?.lastModifiedTime ??
        "",
      expiryDate:
        item.caseQty?.expiryDate ?? item.pieceQty?.expiryDate ?? item.otherQty?.expiryDate ?? "",
      skuDescription: "",
      shelfLife: 0,
      pieceCommittedQty: item.pieceQty?.committedQty ?? 0,
      pieceAvailableQty: item.pieceQty?.availableQty ?? 0,
      pieceUnsellableQty: item.pieceQty?.unsellableQty ?? 0,
      pieceInTransitQty: item.pieceQty?.inTransitQty ?? 0,
      pieceOnRouteQty: item.pieceQty?.onRouteQty ?? 0,
      pieceReturnedQty: item.pieceQty?.returnedQty ?? 0,
      caseCommittedQty: item.caseQty?.committedQty ?? 0,
      caseAvailableQty: item.caseQty?.availableQty ?? 0,
      caseUnsellableQty: item.caseQty?.unsellableQty ?? 0,
      caseInTransitQty: item.caseQty?.inTransitQty ?? 0,
      caseOnRouteQty: item.caseQty?.onRouteQty ?? 0,
      caseReturnedQty: item.caseQty?.returnedQty ?? 0,
      otherCommittedQty: item.otherQty?.committedQty ?? 0,
      otherAvailableQty: item.otherQty?.availableQty ?? 0,
      otherUnsellableQty: item.otherQty?.unsellableQty ?? 0,
      otherInTransitQty: item.otherQty?.inTransitQty ?? 0,
      otherOnRouteQty: item.otherQty?.onRouteQty ?? 0,
      otherReturnedQty: item.otherQty?.returnedQty ?? 0,
    });
  });
  return batchIdWiseStockData;
}

export const batchAndExpiryColumnsMetadata = {
  columns: [
    {
      headerName: "adjustmentDate",
      mappedValue: "adjustmentDate",
      width: 0,
      headerAlign: "left",
      align: "left",
      visibility: false,
    },
    {
      headerName: "Batch ID",
      mappedValue: "batchId",
      width: 200,
      headerAlign: "left",
      align: "left",
    },
    {
      headerName: "SKU Code",
      mappedValue: "skuCode",
      width: 200,
      headerAlign: "left",
      align: "left",
    },
    {
      headerName: "SKU Name",
      mappedValue: "skuDescription",
      width: 400,
      headerAlign: "left",
      align: "left",
    },
    {
      headerName: "Quantity",
      mappedValue: "availableQty",
      width: 160,
      headerAlign: "left",
      align: "left",
      type: "stocks",
    },
    {
      headerName: "Expiry Date",
      mappedValue: "expiryDate",
      width: 160,
      headerAlign: "left",
      align: "left",
    },
  ],
  Actions: [],
};
